import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  PaymentMethod,
  REPORT_MAX_RANGE_DAYS,
  REPORT_PRESETS,
  ReportArea,
  ReportPreset,
  type FinanceBreakdownRow,
  type FinanceReport,
  type FinanceReportQuery,
} from '@optiflow/types';
import { reportAreaVisible } from '@optiflow/shared';
import { Alert } from '@/components/ui/Alert';
import { Card } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { useLocale } from '@/hooks/useLocale';
import { apiErrorCode, apiErrorDetails } from '@/services/apiClient';
import { useAuthStore } from '@/store/authStore';
import { useFinanceReportQuery } from '../api/reports.api';
import { FinanceContent } from '../components/FinanceContent';
import { ReportFilterBar, type ReportFilters } from '../components/ReportFilterBar';
import { ReportMetaLine } from '../components/ReportMetaLine';
import { ReportTabs } from '../components/ReportTabs';

const METHODS = Object.values(PaymentMethod);
const PERIOD_FIELDS = new Set(['preset', 'from', 'to']);

/** The filters this page keeps in the URL, so a reading can be shared or reloaded unchanged. */
function readFilters(params: URLSearchParams) {
  const preset = params.get('preset');
  const method = params.get('method');
  return {
    period: {
      preset: (REPORT_PRESETS.includes(preset as ReportPreset)
        ? preset
        : ReportPreset.THIS_MONTH) as ReportPreset,
      from: params.get('from') ?? '',
      to: params.get('to') ?? '',
      branchId: params.get('branch') ?? '',
      compare: 'none' as const,
    } satisfies ReportFilters,
    currencyCode: params.get('currency') ?? '',
    paymentMethod: (METHODS.includes(method as PaymentMethod) ? method : '') as PaymentMethod | '',
    customerId: params.get('customer') ?? '',
    supplierId: params.get('supplier') ?? '',
    expenseCategoryId: params.get('category') ?? '',
  };
}

/** Every currency the report mentions, base first, so the currency filter offers only real ones. */
function currenciesOf(report: FinanceReport | undefined, base: string, selected: string): string[] {
  const codes = new Set<string>([base]);
  if (selected) codes.add(selected);
  const add = (amounts: { currencyCode: string }[] | undefined) =>
    amounts?.forEach((entry) => codes.add(entry.currencyCode));
  add(report?.collections?.collections);
  add(report?.receivables?.outstanding);
  add(report?.receivables?.scheduled.amount);
  add(report?.payables?.outstanding);
  add(report?.supplierInvoices?.invoiced.amount);
  add(report?.supplierPayments?.paid.amount);
  add(report?.expenses?.expenses.amount);
  return [base, ...[...codes].filter((code) => code !== base).sort()];
}

/**
 * Reports › Finance.
 *
 * Every section is authorised on the server on its own; a section missing here is one this account
 * may not see, or one an active filter cannot narrow. Receivables and payables are always as of now,
 * so the filter bar offers no comparison. Filters are ids in the URL, resolved — and refused — by
 * the server.
 */
export function FinanceReportPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const { period } = filters;
  const grants = useAuthStore((state) => state.user?.permissions);
  const allowed = reportAreaVisible(grants, ReportArea.FINANCE);
  const customReady =
    period.preset !== ReportPreset.CUSTOM || (period.from !== '' && period.to !== '');

  const query: FinanceReportQuery = {
    preset: period.preset,
    ...(period.preset === ReportPreset.CUSTOM ? { from: period.from, to: period.to } : {}),
    branchId: period.branchId || undefined,
    currencyCode: filters.currencyCode || undefined,
    paymentMethod: filters.paymentMethod || undefined,
    customerId: filters.customerId || undefined,
    supplierId: filters.supplierId || undefined,
    expenseCategoryId: filters.expenseCategoryId || undefined,
  };
  const report = useFinanceReportQuery(query, { enabled: customReady && allowed });
  const data = report.data;
  const base = data?.meta.baseCurrencyCode ?? '';

  const write = (next: Partial<ReturnType<typeof readFilters>>) => {
    const merged = { ...filters, ...next, period: { ...period, ...(next.period ?? {}) } };
    const search = new URLSearchParams();
    search.set('preset', merged.period.preset);
    if (merged.period.preset === ReportPreset.CUSTOM) {
      if (merged.period.from) search.set('from', merged.period.from);
      if (merged.period.to) search.set('to', merged.period.to);
    }
    if (merged.period.branchId) search.set('branch', merged.period.branchId);
    for (const [key, value] of [
      ['currency', merged.currencyCode],
      ['method', merged.paymentMethod],
      ['customer', merged.customerId],
      ['supplier', merged.supplierId],
      ['category', merged.expenseCategoryId],
    ] as const) {
      if (value) search.set(key, value);
    }
    setParams(search, { replace: true });
  };

  const field = 'border-line bg-surface text-ink w-full rounded-lg border px-3 py-2 text-sm';
  const label = (row: FinanceBreakdownRow) =>
    (locale === 'ar' && row.labelAr) || row.label || row.id;
  const options = (rows: FinanceBreakdownRow[]) => {
    const seen = new Map<string, string>();
    for (const row of rows) if (row.id && !seen.has(row.id)) seen.set(row.id, label(row) ?? row.id);
    return [...seen.entries()].map(([id, name]) => (
      <option key={id} value={id}>
        {name}
      </option>
    ));
  };
  const suppliers = [
    ...(data?.payables?.bySupplier ?? []),
    ...(data?.supplierInvoices?.bySupplier ?? []),
    ...(data?.supplierPayments?.bySupplier ?? []),
  ];
  const anyFilter =
    filters.currencyCode ||
    filters.paymentMethod ||
    filters.customerId ||
    filters.supplierId ||
    filters.expenseCategoryId;
  const appliedNames = [
    data?.filters.customerName,
    data?.filters.supplierName,
    data?.filters.expenseCategoryName,
  ].filter(Boolean);

  // A refused filter id and a refused period are different mistakes, and each is named in the reader's own language.
  const errorText = () => {
    if (apiErrorCode(report.error) !== 'VALIDATION_FAILED') return t('reports.loadError');
    const fields = apiErrorDetails(report.error).map((detail) => detail.field ?? '');
    return fields.some((name) => PERIOD_FIELDS.has(name))
      ? t('reports.invalidPeriod', { days: REPORT_MAX_RANGE_DAYS })
      : t('reports.finance.invalidFilter');
  };

  return (
    <div className="space-y-5">
      <PageHeader title={t('reports.finance.title')} subtitle={t('reports.finance.subtitle')} />
      <ReportTabs />
      {allowed ? null : <Alert tone="info">{t('reports.finance.noAccess')}</Alert>}
      {allowed ? (
        <>
          <ReportFilterBar
            value={period}
            onChange={(next) => write({ period: { ...next, compare: 'none' } })}
            hideCompare
          />
          <Card className="p-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label
                  htmlFor="finance-currency"
                  className="text-muted mb-1 block text-xs font-medium"
                >
                  {t('reports.finance.filters.currency')}
                </label>
                <select
                  id="finance-currency"
                  className={field}
                  value={filters.currencyCode}
                  onChange={(event) => write({ currencyCode: event.target.value })}
                >
                  <option value="">{t('reports.finance.filters.allCurrencies')}</option>
                  {base
                    ? currenciesOf(data, base, filters.currencyCode).map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))
                    : null}
                </select>
              </div>
              <div>
                <label
                  htmlFor="finance-method"
                  className="text-muted mb-1 block text-xs font-medium"
                >
                  {t('reports.finance.filters.method')}
                </label>
                <select
                  id="finance-method"
                  className={field}
                  value={filters.paymentMethod}
                  onChange={(event) =>
                    write({ paymentMethod: event.target.value as PaymentMethod | '' })
                  }
                >
                  <option value="">{t('reports.finance.filters.allMethods')}</option>
                  {METHODS.map((method) => (
                    <option key={method} value={method}>
                      {t(`sales.methods.${method}`)}
                    </option>
                  ))}
                </select>
              </div>
              {suppliers.length > 0 || filters.supplierId ? (
                <div>
                  <label
                    htmlFor="finance-supplier"
                    className="text-muted mb-1 block text-xs font-medium"
                  >
                    {t('reports.finance.filters.supplier')}
                  </label>
                  <select
                    id="finance-supplier"
                    className={field}
                    value={filters.supplierId}
                    onChange={(event) => write({ supplierId: event.target.value })}
                  >
                    <option value="">{t('reports.finance.filters.allSuppliers')}</option>
                    {options(suppliers)}
                    {filters.supplierId &&
                    !suppliers.some((row) => row.id === filters.supplierId) ? (
                      <option value={filters.supplierId}>
                        {data?.filters.supplierName ?? filters.supplierId}
                      </option>
                    ) : null}
                  </select>
                </div>
              ) : null}
              {data?.expenses || filters.expenseCategoryId ? (
                <div>
                  <label
                    htmlFor="finance-category"
                    className="text-muted mb-1 block text-xs font-medium"
                  >
                    {t('reports.finance.filters.category')}
                  </label>
                  <select
                    id="finance-category"
                    className={field}
                    value={filters.expenseCategoryId}
                    onChange={(event) => write({ expenseCategoryId: event.target.value })}
                  >
                    <option value="">{t('reports.finance.filters.allCategories')}</option>
                    {options(data?.expenses?.byCategory ?? [])}
                    {filters.expenseCategoryId &&
                    !(data?.expenses?.byCategory ?? []).some(
                      (row) => row.id === filters.expenseCategoryId,
                    ) ? (
                      <option value={filters.expenseCategoryId}>
                        {data?.filters.expenseCategoryName ?? filters.expenseCategoryId}
                      </option>
                    ) : null}
                  </select>
                </div>
              ) : null}
            </div>
            {appliedNames.length > 0 ? (
              <p className="text-muted mt-3 text-sm break-words">
                {t('reports.finance.filters.applied', { names: appliedNames.join(' · ') })}
              </p>
            ) : null}
            {anyFilter ? (
              <button
                type="button"
                className="text-brand-700 mt-3 text-sm font-medium hover:underline"
                onClick={() =>
                  write({
                    currencyCode: '',
                    paymentMethod: '',
                    customerId: '',
                    supplierId: '',
                    expenseCategoryId: '',
                  })
                }
              >
                {t('reports.sales.filters.clear')}
              </button>
            ) : null}
          </Card>
        </>
      ) : null}
      {data ? <ReportMetaLine meta={data.meta} /> : null}
      {!customReady && allowed ? (
        <Alert tone="info">{t('reports.filters.chooseDates')}</Alert>
      ) : null}
      {report.isError ? <Alert tone="error">{errorText()}</Alert> : null}
      {report.isPending && customReady && allowed ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-hidden>
          {Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className="bg-surface border-line h-28 animate-pulse rounded-xl border"
            />
          ))}
        </div>
      ) : null}
      {data ? <FinanceContent report={data} query={query} /> : null}
    </div>
  );
}
